import { Component, ReactNode } from "react";
import { Button } from "@/components/ui/buttons/button";

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Called when the user retries. If omitted, retry reloads the whole page. */
  onReset?: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  override state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  override componentDidCatch(error: unknown, info: unknown) {
    console.error("[ErrorBoundary] Caught error:", error, info);
  }

  private handleRetry = () => {
    if (this.props.onReset) {
      this.setState({ hasError: false });
      this.props.onReset();
    } else {
      window.location.reload();
    }
  };

  override render() {
    if (this.state.hasError) {
      return (
        <div className="absolute inset-0 z-[200] flex flex-col items-center justify-center gap-4 bg-pk-night-2 p-6 text-center">
          <h2 className="text-2xl font-black text-white uppercase">
            Something went wrong
          </h2>
          <p className="text-pk-gray-4 text-sm max-w-xs">
            An unexpected error occurred. Retrying usually fixes it.
          </p>
          <Button variant="default" onClick={this.handleRetry}>
            <span className="px-8 py-1">Retry</span>
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}
